import { Outlet, useOutletContext } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { Icon, type IconName } from "../../components/icons";
import { Alert, PageHeader, Tabs, Tag } from "../../components/ui";
import { formatNumber, formatRelative } from "../../lib/format";
import { useDomainsOverview } from "../../lib/queries";
import type { DomainsOverview } from "../../lib/types";

/**
 * The domain product, from the operator's side.
 *
 * Five numbers first, because they are what an operator scans before deciding
 * whether to read further: which registrar this console talks to, how much of
 * the catalogue is on sale, how many names are held, how many renew within
 * the sweep's window, and how many orders need a person. Then three tabs —
 * Domains for the ticket that says "my domain", Catalogue for what is sold
 * and at what margin, Orders for the money — each a real route, so a link
 * pasted into a channel lands a colleague on the same panel.
 */
export interface DomainsTabContext {
  /** Superadmin: the catalogue, settling an order. */
  canWrite: boolean;
  /** Support and up: marking a flagged name as looked at. */
  canReview: boolean;
  overview: DomainsOverview | undefined;
}

export const useDomainsTab = () => useOutletContext<DomainsTabContext>();

export function DomainsPage() {
  const { role } = useAuth();
  const canWrite = role === "superadmin";
  const canReview = role === "superadmin" || role === "support";
  const overview = useDomainsOverview();
  const data = overview.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Domains"
        description="Names sold through the platform: what is on the shelf, who holds what, and the orders behind them."
        action={
          data && (
            <Tag tone={data.registrarProduction ? "warn" : "neutral"}>
              {data.registrarConfigured
                ? data.registrarProduction
                  ? "production registrar"
                  : "sandbox registrar"
                : "registrar not configured"}
            </Tag>
          )
        }
      />

      {overview.error && <Alert>{overview.error.message}</Alert>}
      {data && !data.registrarConfigured && (
        <Alert tone="warning">
          `OPENPROVIDER_USERNAME` and `OPENPROVIDER_PASSWORD` are unset on this deployment. Nothing here can be synced or sold until they are.
        </Alert>
      )}

      {data && (
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line lg:grid-cols-4">
          <Stat
            label="On sale"
            value={data.catalogue.onSale}
            detail={
              data.catalogue.lastSyncedAt
                ? `${data.catalogue.sellable} sellable of ${data.catalogue.extensions} · synced ${formatRelative(data.catalogue.lastSyncedAt)}`
                : "never synced"
            }
            icon="plans"
          />
          <Stat
            label="Active domains"
            value={data.domains.active}
            detail={[data.domains.pending && `${data.domains.pending} pending`, data.domains.expired && `${data.domains.expired} expired`].filter(Boolean).join(" · ") || "none pending"}
            icon="globe"
          />
          <Stat
            label="Renew within 30 days"
            value={data.domains.renewalsDue}
            detail={data.domains.renewalsFailing ? `${data.domains.renewalsFailing} failing` : "the sweep's window"}
            icon="clock"
          />
          <Stat
            label="Needing a person"
            value={data.stuckOrders + data.flagged + data.domains.renewalsFailing}
            detail={[
              data.stuckOrders && `${data.stuckOrders} stuck order${data.stuckOrders === 1 ? "" : "s"}`,
              data.flagged && `${data.flagged} flagged name${data.flagged === 1 ? "" : "s"}`,
              data.domains.renewalsFailing && `${data.domains.renewalsFailing} renewal${data.domains.renewalsFailing === 1 ? "" : "s"} failing`,
            ].filter(Boolean).join(" · ") || "nothing"}
            icon="warning"
          />
        </div>
      )}

      <Tabs
        items={[
          { to: ".", label: "Domains", end: true },
          { to: "catalogue", label: "Catalogue" },
          { to: "orders", label: "Orders" },
        ]}
      />

      <Outlet context={{ canWrite, canReview, overview: data } satisfies DomainsTabContext} />
    </div>
  );
}

function Stat({ label, value, detail, icon }: { label: string; value: number; detail?: string; icon: IconName }) {
  return (
    <div className="bg-surface px-4 py-3.5 sm:px-5 sm:py-4">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <p className="min-w-0 text-sm font-medium text-fg-subtle">{label}</p>
        <span className="grid size-8 shrink-0 place-items-center rounded-md border border-line bg-raised text-fg-subtle">
          <Icon name={icon} className="size-4" />
        </span>
      </div>
      <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl">{formatNumber(value)}</p>
      {detail && <p className="mt-1 text-xs leading-5 text-fg-subtle">{detail}</p>}
    </div>
  );
}
